import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { describeCast, startCast, watchCast } from "../playback/cast";
import { favoriteCount, getFavorites, isFavorite, toggleFavorite } from "../storage/livetv-favorites.js";
import { apiUrl } from "../config";

// Live TV — channels from the iptv-org project (publicly available FTA
// streams). Grid of channel cards with logos, grouped filter tabs (playlist =
// country/category; groups within it), and a player with automatic fallback:
// direct URL first, same-origin proxy when the CDN blocks browser origins,
// hls.js for HLS on non-Safari, native HLS on iPhone.

const PAGE_SIZE = 60;

// Fire TV mode (?tv=1): expose a tiny bridge so the app's BACK key can close
// the channel player before leaving the page (the APK calls window.PokuTv.back()).
if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tv") === "1") {
  window.PokuTv = {
    back() {
      const el = document.querySelector(".livetv-player-overlay");
      if (el) {
        el.dispatchEvent(new MouseEvent("click", { bubbles: false }));
        return true; // player was open — now closed
      }
      return false; // nothing to close; caller should leave the page
    },
  };
}

export default function LiveTVScreen() {
  const [playlists, setPlaylists] = useState([]);
  const [active, setActive] = useState("mal");
  const [channels, setChannels] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [group, setGroup] = useState("All");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [playing, setPlaying] = useState(null); // { key, name, url, kind, pl }
  const [favVersion, setFavVersion] = useState(0);

  const toggleFav = useCallback((pl, ch) => {
    toggleFavorite(`${pl}:${ch.key}`, { name: ch.name, logo: ch.logo, group: ch.group });
    setFavVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    fetch("/api/livetv/playlists")
      .then((r) => r.json())
      .then((d) => setPlaylists(d.playlists || []))
      .catch(() => setPlaylists([]));
  }, []);

  useEffect(() => {
    if (active === "favs") return; // handled by the sync effect below
    setChannels(null);
    setError(null);
    setGroup("All");
    setQuery("");
    setVisible(PAGE_SIZE);
    fetch(`/api/livetv/channels?pl=${encodeURIComponent(active)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status === 502 ? "Playlist source unreachable" : `HTTP ${r.status}`))))
      .then((d) => setChannels(d.channels || []))
      .catch((e) => setError(e.message));
  }, [active]);

  // Favourites tab: rendered purely client-side from localStorage; re-syncs
  // whenever a star is toggled so the grid updates instantly.
  useEffect(() => {
    if (active !== "favs") return;
    setError(null);
    setGroup("All");
    setQuery("");
    setVisible(PAGE_SIZE);
    const entries = Object.entries(getFavorites());
    entries.sort((a, b) => (b[1].at || 0) - (a[1].at || 0));
    setChannels(
      entries.map(([fullKey, m]) => ({
        key: fullKey.slice(fullKey.indexOf(":") + 1),
        pl: fullKey.split(":")[0],
        name: m.name,
        logo: m.logo,
        group: m.group || "Favourite",
        status: "unchecked",
      })),
    );
  }, [active, favVersion]);

  const groups = useMemo(() => {
    if (!channels) return [];
    const counts = new Map();
    for (const c of channels) counts.set(c.group, (counts.get(c.group) || 0) + 1);
    return ["All", ...[...counts.entries()].sort((a, b) => b[1] - a[1]).map(([g]) => g)];
  }, [channels]);

  const filtered = useMemo(() => {
    if (!channels) return [];
    let list = channels;
    if (group !== "All") list = list.filter((c) => c.group === group);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q));
    // Healthy channels first, unchecked next, dead last; alphabetical within
    // each band so 3,000-channel lists (English/USA) are browsable.
    const rank = (s) => (s === "ok" ? 0 : s === "dead" ? 2 : 1);
    return [...list].sort((a, b) => rank(a.status) - rank(b.status) || a.name.localeCompare(b.name));
  }, [channels, group, query]);

  const play = useCallback(
    (ch) => {
      const pl = ch.pl || active;
      fetch(`/api/livetv/play?pl=${encodeURIComponent(pl)}&key=${encodeURIComponent(ch.key)}`)
        .then((r) => r.json())
        .then((d) => setPlaying({ ...d, pl, logo: ch.logo, status: ch.status }))
        .catch(() => {});
    },
    [active],
  );

  return (
    <main className="page livetv-page">
      <div className="page-content">
      <h1 className="page-heading" style={{ marginBottom: 6 }}>
        Live TV
      </h1>
      <p className="muted" style={{ margin: "0 0 14px" }}>
        Free live channels — Malayalam, India, English, UK, movies, sports and more.
      </p>

      {favoriteCount() > 0 && (
        <div className="livetv-tabs" role="tablist" style={{ marginBottom: 10 }}>
          <button
            role="tab"
            aria-selected={active === "favs"}
            className={`chip${active === "favs" ? " active" : ""}`}
            onClick={() => setActive("favs")}
          >
            ★ Favourites
          </button>
        </div>
      )}

      <div className="livetv-tabs" role="tablist">
        {playlists
          .filter((p) => p.section !== "custom" || p.channels > 0)
          .map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={active === p.id}
              className={`chip${active === p.id ? " active" : ""}`}
              onClick={() => setActive(p.id)}
            >
              {p.id === "custom" ? "My Channels" : p.label}
            </button>
          ))}
      </div>

      <div className="livetv-toolbar">
        <input
          className="livetv-search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setVisible(PAGE_SIZE);
          }}
          placeholder="Search channels…"
        />
        {channels && (
          <select
            className="livetv-group"
            value={group}
            onChange={(e) => {
              setGroup(e.target.value);
              setVisible(PAGE_SIZE);
            }}
          >
            {groups.map((g) => (
              <option key={g} value={g}>
                {g === "All" ? `All groups (${channels.length})` : `${g}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <p className="source-check bad">Could not load channels: {error}</p>}
      {!channels && !error && (
        <div className="livetv-grid">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="livetv-card skeleton" />
          ))}
        </div>
      )}

      {channels && (
        <>
          <div className="livetv-grid">
            {filtered.slice(0, visible).map((ch) => (
              <div key={`${ch.pl || active}:${ch.key}`} className="livetv-card-wrap">
                <button className={`livetv-card${ch.status === "dead" ? " dead" : ""}`} onClick={() => play(ch)} title={ch.name}>
                  {ch.status === "ok" && <span className="live-dot" aria-hidden="true" />}
                  <span className="livetv-logo">
                    {ch.logo ? <img src={ch.logo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.querySelector(".livetv-logo-fallback")?.removeAttribute("hidden"); }} /> : null}
                    <span className="livetv-logo-fallback" hidden={Boolean(ch.logo)}>{ch.name.slice(0, 2).toUpperCase()}</span>
                  </span>
                  <span className="livetv-name">{ch.name}</span>
                  <span className="livetv-meta">{ch.group}</span>
                </button>
                <button
                  className={`livetv-fav${isFavorite(`${ch.pl || active}:${ch.key}`) ? " on" : ""}`}
                  aria-label={isFavorite(`${ch.pl || active}:${ch.key}`) ? `Remove ${ch.name} from favourites` : `Add ${ch.name} to favourites`}
                  title={isFavorite(`${ch.pl || active}:${ch.key}`) ? "Remove from favourites" : "Add to favourites"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFav(ch.pl || active, ch);
                    if (active === "favs") setChannels((list) => (list || []).filter((c) => c.key !== ch.key));
                  }}
                >
                  ★
                </button>
              </div>
            ))}
          </div>
          {filtered.length === 0 && <p className="muted">No channels match.</p>}
          {visible < filtered.length && (
            <div className="player-actions" style={{ justifyContent: "center", marginTop: 18 }}>
              <button className="btn btn-ghost" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Show more ({filtered.length - visible} left)
              </button>
            </div>
          )}
        </>
      )}

      {playing && <ChannelPlayer channel={playing} onClose={() => setPlaying(null)} />}
      </div>
    </main>
  );
}

// Full-screen-ish overlay player with graceful fallbacks:
//   1. Direct URL (hls.js for HLS, native for files)
//   2. Same-origin proxy (CDNs that block non-browser origins)
// Reports success/failure back so channel health stays current.
function ChannelPlayer({ channel, onClose }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [mode, setMode] = useState("direct"); // direct -> proxy
  const [state, setState] = useState(channel.status === "dead" ? "connecting" : "connecting"); // connecting|playing|error
  const [msg, setMsg] = useState(channel.status === "dead" ? "This channel was reported offline — trying anyway…" : "");
  // PoKu control bar state — same icon language as the movie players
  // (play/pause, mute + volume, fullscreen; no seek — live TV).
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [controls, setControls] = useState(true);
  // Casting a channel to a TV. The button appears only when the browser has a
  // native picker AND the element holds a plain URL a receiver can fetch
  // itself: on Safari/iPhone the channels play as native HLS (castable), while
  // Chrome/Android run them through hls.js' MediaSource and get no button —
  // see playback/cast.js.
  const [cast, setCast] = useState({ route: null, castable: false, connected: false, available: true });
  const lastActivityRef = useRef(Date.now());

  useEffect(() => watchCast(videoRef.current, setCast), []);

  const toggleCast = async () => {
    const v = videoRef.current;
    if (!v) return;
    lastActivityRef.current = Date.now();
    const result = await startCast(v);
    setCast({ ...describeCast(v), ...(result === "unavailable" ? { available: false } : {}) });
  };

  // Auto-hide the bar after 3s of inactivity while playing; any pointer or
  // key activity re-shows it (same behavior as the movie players).
  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); setControls(true); };
    const opts = { passive: true };
    window.addEventListener("pointermove", bump, opts);
    window.addEventListener("pointerdown", bump, opts);
    window.addEventListener("keydown", bump, opts);
    window.addEventListener("touchstart", bump, opts);
    const t = setInterval(() => {
      const v = videoRef.current;
      if (v && !v.paused && Date.now() - lastActivityRef.current >= 3000) setControls(false);
    }, 500);
    return () => {
      window.removeEventListener("pointermove", bump, opts);
      window.removeEventListener("pointerdown", bump, opts);
      window.removeEventListener("keydown", bump, opts);
      window.removeEventListener("touchstart", bump, opts);
      clearInterval(t);
    };
  }, []);

  // Keep bar state in sync with the element (autoplay latency, stalls…).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVol = () => { setVolume(v.muted ? 0 : v.volume); setMuted(v.muted); };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("volumechange", onVol);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("volumechange", onVol);
    };
  }, [state === "playing"]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  };
  const toggleFullscreen = () => {
    const el = document.querySelector(".livetv-player");
    if (!el) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {
        const v = videoRef.current;
        if (v && v.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone
      });
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else {
      const v = videoRef.current;
      if (v && v.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone
    }
  };
  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  const streamUrl = useMemo(() => {
    if (!channel.url) return null;
    // Absolute in the packaged app (apiUrl is identity on the web): this ends up
    // as <video src>/hls.js loadSource, which the fetch bridge cannot rewrite.
    return mode === "direct"
      ? channel.url
      : apiUrl(`/api/livetv/proxy?pl=${encodeURIComponent(channel.pl)}&key=${encodeURIComponent(channel.key)}&seg=${encodeURIComponent(channel.url)}`);
  }, [channel, mode]);

  useEffect(() => {
    if (!streamUrl) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    let disposed = false;
    let hls = null;

    const report = (ok) => {
      fetch("/api/livetv/report", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ key: channel.key, ok }),
      }).catch(() => {});
    };

    const fail = (why) => {
      if (disposed) return;
      report(false);
      if (mode === "direct") {
        setMode("proxy"); // one automatic fallback attempt
        setMsg(`Direct stream blocked — retrying through the site's relay…`);
      } else {
        setState("error");
        setMsg("This channel is not responding right now. Try another one — channels go up and down all the time.");
      }
    };

    if (channel.kind === "hls" && !v.canPlayType("application/vnd.apple.mpegurl")) {
      import("hls.js")
        .then((m) => m.default)
        .then((Hls) => {
          if (disposed) return;
          if (!Hls.isSupported()) {
            v.src = streamUrl;
            return;
          }
          hls = new Hls({ enableWorker: true, lowLatencyMode: true, backBufferLength: 60 });
          hlsRef.current = hls;
          hls.on(Hls.Events.ERROR, (_e, data) => {
            if (data?.fatal) fail(data.details || "stream error");
          });
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            v.play().catch(() => {});
          });
          hls.loadSource(streamUrl);
          hls.attachMedia(v);
        })
        .catch(() => fail("player failed to load"));
    } else {
      v.src = streamUrl;
      v.play().catch(() => {});
    }

    const onPlaying = () => {
      if (disposed) return;
      setState("playing");
      setMsg("");
      report(true);
    };
    const onError = () => fail(null);
    const onStalled = () => {};
    v.addEventListener("playing", onPlaying);
    v.addEventListener("error", onError);
    v.addEventListener("stalled", onStalled);
    return () => {
      disposed = true;
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("error", onError);
      v.removeEventListener("stalled", onStalled);
      if (hls) {
        hls.destroy();
        hlsRef.current = null;
      }
      v.removeAttribute("src");
    };
  }, [streamUrl, channel.kind, channel.key, mode]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !window.PokuTv) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.title = channel.name ? `${channel.name} — PoKu Live` : "PoKu Live";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.title = "PoKu — Watch Movies & TV Shows";
    };
  }, [channel.name, onClose]);

  return (
    <div className="livetv-player-overlay" onClick={onClose} role="dialog" aria-label={channel.name}>
      <div className="livetv-player" onClick={(e) => e.stopPropagation()}>
        <video ref={videoRef} className="livetv-video" autoPlay playsInline x-webkit-airplay="allow" />
        {/* Back arrow — same style as the movie players; fades with the bar
            after 3s idle so nothing sits over the picture while watching. */}
        <button
          type="button"
          className={`player-back${controls ? "" : " hidden"}`}
          aria-label="Back to channels"
          title="Back"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
        >
          ←
        </button>
        {/* PoKu control bar — same icon language as the movie players:
            play/pause, mute + volume, fullscreen. No seek — live TV. */}
        <div className={`torrent-ui livetv-ui${controls ? "" : " hidden"}`}>
          <div className="torrent-row" onClick={(e) => e.stopPropagation()}>
            <div className="torrent-group">
              <button
                type="button"
                className="torrent-btn torrent-btn-play"
                aria-label={playing ? "Pause" : "Play"}
                title={playing ? "Pause" : "Play"}
                onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={playing ? "M6 19h4V5H6v14zm8-14v14h4V5h-4z" : "M8 5v14l11-7z"} />
                </svg>
              </button>
              <button
                type="button"
                className="torrent-btn"
                aria-label={muted ? "Unmute" : "Mute"}
                title={muted ? "Unmute" : "Mute"}
                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={muted || volume === 0
                    ? "M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                    : "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"} />
                </svg>
              </button>
              <div className="torrent-vol">
                <input
                  type="range"
                  className="torrent-seek"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(e) => {
                    const v = videoRef.current;
                    if (!v) return;
                    const next = Number(e.target.value);
                    v.volume = next;
                    v.muted = next === 0;
                    setVolume(next);
                    setMuted(v.muted);
                    lastActivityRef.current = Date.now();
                  }}
                  aria-label="Volume"
                />
              </div>
              <span className="torrent-vol-pct">{muted ? 0 : Math.round(volume * 100)}%</span>
            </div>
            <div className="torrent-group">
              {cast.route && cast.castable && (
                <button
                  type="button"
                  className={`torrent-btn${cast.connected ? " on" : ""}`}
                  aria-label={
                    cast.connected
                      ? "Casting to a TV"
                      : cast.available
                        ? "Cast to a TV"
                        : "Cast — no devices found"
                  }
                  title={cast.connected ? "Casting to a TV" : cast.available ? "Cast to a TV" : "Cast — no devices found"}
                  onClick={(e) => { e.stopPropagation(); toggleCast(); }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className="torrent-btn"
                aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={fullscreen
                    ? "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                    : "M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"} />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {state !== "playing" && (
          <div className="player-overlay livetv-status">
            {state === "connecting" && (
              <>
                <div className="spinner" />
                <p className="status-label">{msg || "Tuning in…"}</p>
              </>
            )}
            {state === "error" && (
              <>
                <h2 className="status-title">Channel unavailable</h2>
                <p className="status-label">{msg}</p>
                <div className="player-actions">
                  <button className="btn btn-primary" onClick={() => { setState("connecting"); setMode("direct"); }}>
                    Retry
                  </button>
                  <button className="btn btn-ghost" onClick={onClose}>
                    Back to channels
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
